import { QueryClient, useMutation } from "@tanstack/react-query";
import { updateUser } from "../../../api/user";
import { assign } from "lodash";
import toast from "react-hot-toast";
import { Queries, getAuthenticatedUserQuerie } from "../../../types/queries";
import { V3GenericErrorHandler } from "../../../api/error-handler";
import { NotificationTargets } from "../../../types/notification";
import { UserResponse } from "@authory-packages/core";
import { AxiosError } from "axios";

export const getUpdateUser = (queryClient: QueryClient, token: string) =>
    useMutation({
        mutationFn: updateUser,
        onMutate: ({ data, skipOptimisticUpdate = false }) => {
            const oldUser = queryClient.getQueryData<UserResponse>(getAuthenticatedUserQuerie(token));

            // Generate new user
            if (skipOptimisticUpdate) return oldUser;

            const newUser = assign({}, oldUser, data);

            // Optimistically update to the new value
            queryClient.setQueryData(getAuthenticatedUserQuerie(token), newUser);

            return oldUser;
        },
        // If the mutation fails, use the value returned from onMutate to roll back
        onError: (err: AxiosError, _, oldData) => {
            queryClient.setQueryData(getAuthenticatedUserQuerie(token), oldData);
            V3GenericErrorHandler(toast.error)(err);
        },
        onSuccess: async (data, variables) => {
            if (variables.skipAllUpdates) return;

            queryClient.setQueryData(getAuthenticatedUserQuerie(token), data);

            if (data.hasGdprPrivacyPolicy) {
                queryClient.invalidateQueries({ queryKey: [Queries.GDPRSettings] });
            }

            if (variables.target === NotificationTargets.UserNameUpdated) {
                toast.success("Name updated");
            } else if (variables.target === NotificationTargets.UserAliasUpdated) {
                toast.success("Alias updated");
            } else if (variables.target === NotificationTargets.UserEmailUpdated) {
                toast.success("Email updated");
            } else if (variables.target === NotificationTargets.UserPasswordUpdated) {
                toast.success("Password updated");
            } else if (variables.target === NotificationTargets.UserGDPRSettingsUpdated) {
                if (data.hasGdprPrivacyPolicy) toast.success("GDPR settings enabled");
                else toast.success("GDPR settings disabled");
            } else if (variables.target === NotificationTargets.UserGDPRImprintUpdated) {
                toast.success("Imprint updated");
            } else if (variables.target === NotificationTargets.UserGDPRPolicyUpdated) {
                toast.success("Privacy policy updated");
            } else if (variables.target === NotificationTargets.SEO) {
                toast.success("Preview updated");
            } else if (variables.target === NotificationTargets.ProfileShareChange) {
                toast.success("Portfolio share settings updated");
            } else if (variables.target === NotificationTargets.ProfileBioUpdated) {
                toast.success("Bio updated");
            } else if (variables.target === NotificationTargets.ProfileCoverUpdated) {
                toast.success("Cover updated");
            } else if (variables.target === NotificationTargets.ProfileLayoutUpdated) {
                toast.success("Header layout updated");
            } else if (variables.target === NotificationTargets.ProfileContentLayoutUpdated) {
                toast.success("Content layout updated");
            } else if (variables.target === NotificationTargets.ProfileSocialMediaLinks) {
                toast.success("Social media links updated");
            } else if (variables.target === NotificationTargets.ProfileHighlightButton) {
                toast.success("Highlight Button updated");
            } else if (variables.target === NotificationTargets.ProfileEmailSubscribeEnabled) {
                toast.success("Email subscribe enabled");
            } else if (variables.target === NotificationTargets.ProfileEmailSubscribeDisabled) {
                toast.success("Email subscribe disabled");
            } else if (variables.target === NotificationTargets.ProfileFilterChange) {
                toast.success("Profile filters updated");
            } else if (variables.target === NotificationTargets.ProfileCollectionsLayoutUpdated) {
                toast.success("Collections layout updated");
            } else if (variables.target === NotificationTargets.BrandingUpdated) {
                toast.success("Branding updated");
            } else if (variables.target === NotificationTargets.GoogleIntegrationsUpdated) {
                toast.success("Google Integrations updated");
            } else if (variables.target === NotificationTargets.ContentItemShareUpdated) {
                toast.success("Content Item share updated");
            }
        }
    });
